// attack-weapon-resolution.ts — the browser's share of get_weapon_for_attack_ability() (janus/core/get_weapon_for_attack_ability.php)
// for the R-025 S5 authoritative gate. janus judges an attack_ability / opportunity_attack_ability on a COMPOSITE row: the weapon it
// resolves, stamped item_id_used = the weapon's inventory id, with the ability nested under attack_ability_arr. The page can build
// that row when the fold-out PINNED a weapon PHP accepts as-is, and (R-025 S6, 2026-09-17) when PHP's own walk resolves: the
// attachment-gate slot walk, the default hand-weapon walk (resolve_caster_default_item_of_type), and the natural-weapon pick
// (resolve_natural_weapon_id_for_attack_roll — ranked by average damage, head-attack gate) over definitions the page holds.
// Unavailable bindings return a named UNKNOWN site before dice; callers refuse them.
// Parity: tools/test/janus-ability-preconditions-parity.test.mjs (the oracle rows with `attack`, resolved by the REAL PHP resolver).
import { layerListEntryUuid } from './spell-components';
import { THROW_HAND_SLOTS } from './throw-object-gate';
import { heldInventory, looselyZero } from '../inventory/held-inventory.js';

type Row = Record<string, unknown>;

export interface AttackWeaponReaders {
  itemField?: (row: Row | null, key: string) => unknown;
  /** layer_value() */
  value: (row: Row | null, key: string) => unknown;
  /** layer_entries() */
  entries: (row: Row | null, key: string) => unknown[];
  /** inventory_field() */
  inventoryField: (row: Row | null, key: string) => unknown;
  /** item_field($row, 'item_type') for an inventory row (the gear-template bridge); '' when unread. */
  itemType: (row: Row) => string;
  /** query_full_item_arr() for a natural weapon id: the page's catalog definition; null when unheld (the walk stands down). */
  resolveItem?: (id: string) => Row | null;
  /** ft_creature_in_combat() for the caster (the head-attack gate); null when the page cannot tell. */
  inCombat?: (caster: Row) => boolean | null;
}

export type AttackWeaponResolution = { composite: Row } | { unknown: string };

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
// PHP compares the RAW slot string (in_array against the canonical ids): no case / whitespace folding.
const HAND_SLOTS = new Set<string>([THROW_HAND_SLOTS.both_hands, THROW_HAND_SLOTS.main_hand, THROW_HAND_SLOTS.off_hand]);
const ATTACK_TYPES = new Set(['attack_ability', 'opportunity_attack_ability']);
const HEAD_SLOT = '3805BDD7-3461-DDDB-F445-922A5A992987';
/** ft_ability_attachment_axis_lookup(): the normalised, lower-cased entry set of one attachment axis. */
function axisLookup(ability: Row, layer: string, read: AttackWeaponReaders): Set<string> {
  const lookup = new Set<string>();
  for (const entry of read.entries(ability, layer)) {
    const value = typeof entry === 'string' ? entry : layerListEntryUuid(entry);
    if (value !== '') lookup.add(String(value).toLowerCase());
  }
  return lookup;
}

/**
 * The composite janus judges for an attack cast whose weapon the fold-out pinned; null when the ability is not an attack ability
 * (the gate judges it as it stands).
 */
export function resolveAttackComposite(
  input: { ability: Row; caster: Row; weaponInventoryId: unknown },
  read: AttackWeaponReaders,
): AttackWeaponResolution | null {
  const { ability, caster } = input;
  const abilityType = String(read.itemField ? read.itemField(ability, 'item_type') : scalar(record(ability.items)?.item_type ?? ability.item_type) ?? '');
  // PHP types the row through item_field()'s bridge; a row the page cannot type might be an attack (Codex review).
  if (abilityType === '') return { unknown: 'ability-type-unread' };
  if (!ATTACK_TYPES.has(abilityType)) return null;
  // Branch 1: a draw-sheathed attack auto-equips the bound weapon (a write) before anything is judged.
  const drawSheathed = scalar(read.value(ability, 'draw_sheathed_weapon_for_attack_ability'));
  if (drawSheathed !== null && drawSheathed !== undefined && drawSheathed !== '' && drawSheathed !== 0 && drawSheathed !== '0' && drawSheathed !== false) {
    return { unknown: 'attack-draw-sheathed' };
  }
  const pinned = typeof input.weaponInventoryId === 'string' ? input.weaponInventoryId : '';
  const handCounts = ['count_main_hand_weapon_equipped', 'count_off_hand_weapon_equipped', 'count_both_hands_weapon_equipped'];
  const carried = heldInventory(caster, read.value);
  if (!carried) return { unknown: 'attack-weapon-inventory' };
  // "No weapons equipped" warns and exits before janus reaches the gate — not a gate refusal; leave it to the server.
  const natural = [...read.entries(caster, 'natural_weapon'), ...((): unknown[] => {
    const derived = record(record(caster.computed)?.creature)?.natural_weapon;
    return Array.isArray(derived) ? derived : [];
  })()];
  if (handCounts.every(key => looselyZero(read.value(caster, key)))
    && natural.length === 0) {
    return { unknown: 'attack-no-weapon' };
  }
  const abilityId = String(read.itemField ? read.itemField(ability, 'item_id') : scalar(record(ability.items)?.item_id ?? ability.item_id) ?? '');
  const inventoryRows = carried.map(entry => record(entry)).filter((entry): entry is Row => entry !== null);
  const typeOf = (candidate: Row): string | null => { const type = read.itemType(candidate); return type === '' ? null : type; };
  // An inventory row as the resolved weapon: stamp the bridge-read type (ftItemsCol ≙ item_field) so the gate types it as PHP does.
  // A row without an inventory_id is stamped with its template id in some PHP branches: not reproduced — left to PHP.
  const inventoryComposite = (weapon: Row, type: string): AttackWeaponResolution => {
    const inventoryId = String(read.inventoryField(weapon, 'inventory_id') ?? '');
    if (inventoryId === '') return { unknown: 'attack-weapon-no-inventory-id' };
    // Keep a lean inventory reference lean: an identity-only items bucket would
    // disable the page reader's template bridge and hide its inherited layers.
    return { composite: { ...weapon, ...(record(weapon.items) ? { items: { ...record(weapon.items), item_type: type } } : {}),
      item_id_used: [inventoryId], attack_ability_arr: { ...ability, item_id_used: [abilityId] } } };
  };
  // A natural weapon as the resolved weapon: janus gates query_full_item_arr(id) stamped with item_id_used = that id.
  const naturalComposite = (id: string, definition: Row): AttackWeaponResolution => ({ composite: { ...definition, item_id_used: [id],
    attack_ability_arr: { ...ability, item_id_used: [abilityId] } } });
  const ids = axisLookup(ability, 'attached_equipped_weapon', read);
  const forms = axisLookup(ability, 'attached_weapon_form', read);
  const gated = ids.size > 0 || forms.size > 0;

  /** ft_weapon_matches_ability_attachment_gate() over a row that carries its layers; null = the page cannot tell. */
  const matchesGate = (candidate: Row): boolean | null => {
    const template = String(read.inventoryField(candidate, 'inventory_item_id') ?? scalar(record(candidate.items)?.item_id) ?? '').trim().toLowerCase();
    if (template !== '' && ids.has(template)) return true;
    if (forms.size === 0) return false;
    if (!record(candidate.item_layer_types)) return null; // PHP resolves a layerless row through the catalog
    const form = String(layerListEntryUuid(read.value(candidate, 'natural_weapon_form') ?? '')).toLowerCase();
    return form !== '' && forms.has(form);
  };
  /** The pin PHP honours: equipped in a hand slot, not absorbed, a weapon, and past the ability's own gate. */
  const pinResolution = (): AttackWeaponResolution | 'walk' => {
    if (!UUID.test(pinned)) return 'walk';
    const row = inventoryRows.find(entry => String(read.inventoryField(entry, 'inventory_id') ?? '') === pinned) ?? null;
    if (!row) return 'walk';
    if (Number(scalar(read.inventoryField(row, 'inventory_private')) ?? 0) === 2) return 'walk';
    if (!HAND_SLOTS.has(String(read.inventoryField(row, 'equipped') ?? ''))) return 'walk';
    const rowType = typeOf(row);
    if (rowType === null) return { unknown: 'attack-weapon-type-unread' };
    if (rowType !== 'weapon' && rowType !== 'natural_weapon') return 'walk';
    if (gated) {
      const match = matchesGate(row);
      if (match === null) return { unknown: 'attack-weapon-form-def' };
      return match ? inventoryComposite(row, rowType) : 'walk';
    }
    if (read.entries(ability, 'if_weapon_classification_equipped_active').length > 0
      || 'if_weapon_classification_equipped_active' in (record(ability.item_layer_types) ?? {})) {
      // The classification pin compares each RAW entry with the row's scalar classification (PHP ==).
      const classification = scalar(read.value(row, 'weapon_classification'));
      for (const condition of read.entries(ability, 'if_weapon_classification_equipped_active')) {
        if (typeof condition !== 'string') return { unknown: 'attack-weapon-classification-shape' };
        if (typeof classification === 'string' && classification === condition) return inventoryComposite(row, rowType);
      }
      return 'walk';
    }
    return inventoryComposite(row, rowType);
  };
  /** resolve_natural_weapon_id_for_attack_roll(): best average damage, index tie-break, head-slot naturals out while the gate is live. */
  const naturalPick = (filtered: boolean): AttackWeaponResolution | '' => {
    const inCombat = read.inCombat ? read.inCombat(caster) : null;
    const headAttack = Number(scalar(record(caster.encounter_creatures)?.head_attack) ?? 0) === 1;
    const multipleHeads = read.value(caster, 'multiple_head_attacks') !== null && read.value(caster, 'multiple_head_attacks') !== undefined;
    if (headAttack && !multipleHeads && inCombat === null) return { unknown: 'attack-natural-combat' };
    const headGate = inCombat === true && headAttack && !multipleHeads;
    let best: { id: string; definition: Row; score: number } | null = null;
    const seen = new Set<string>();
    for (const entry of natural) {
      const id = typeof entry === 'string' ? entry : layerListEntryUuid(entry);
      if (id === '') continue;
      // creature_natural_weapon_entries() dedupes the pristine layer against the computed list.
      if (seen.has(id.toLowerCase())) continue;
      seen.add(id.toLowerCase());
      const nested = record(entry);
      const definition = nested && record(nested.item_layer_types) ? nested : (read.resolveItem ? read.resolveItem(id) : null);
      if (!definition) return { unknown: 'attack-natural-def' }; // PHP may rank a definition the page does not hold
      if (filtered) {
        const idAllowed = ids.has(id.toLowerCase());
        const form = forms.size > 0 ? String(layerListEntryUuid(read.value(definition, 'natural_weapon_form') ?? '')).toLowerCase() : '';
        if (!idAllowed && !(form !== '' && forms.has(form))) continue;
      }
      if (headGate && upper(layerListEntryUuid(read.value(definition, 'equipment_slot') ?? '')) === HEAD_SLOT) continue;
      const int = (value: unknown, fallback: number): number => { const v = scalar(value); const n = v === null || v === undefined ? fallback : Math.trunc(Number(v)); return Number.isFinite(n) ? n : 0; };
      const dice = int(read.value(definition, 'dice'), 1); const die = int(read.value(definition, 'die'), 8); const mod = int(read.value(definition, 'mod'), 0);
      const score = dice > 0 && die > 0 ? dice * (die + 1) / 2 + mod : mod;
      if (!best || score > best.score) best = { id, definition, score };
    }
    if (!best) return '';
    // PHP ranks on the nested entry when it carries layers but GATES query_full_item_arr(id): the catalog definition (Codex review).
    const catalog = read.resolveItem ? read.resolveItem(best.id) : null;
    return catalog ? naturalComposite(best.id, catalog) : { unknown: 'attack-natural-def' };
  };

  if (gated) {
    // Branch 2, the ATTACHMENT GATE: the honoured pin, else the both → main → off slot walk, else a matching natural weapon.
    const pin = pinResolution();
    if (pin !== 'walk') return pin;
    for (const slot of [THROW_HAND_SLOTS.both_hands, THROW_HAND_SLOTS.main_hand, THROW_HAND_SLOTS.off_hand]) {
      for (const candidate of inventoryRows) {
        if (String(read.inventoryField(candidate, 'equipped') ?? '') !== slot) continue;
        if (Number(scalar(read.inventoryField(candidate, 'inventory_private')) ?? 0) === 2) continue;
        const type = typeOf(candidate);
        if (type === null) return { unknown: 'attack-weapon-type-unread' };
        if (type !== 'weapon' && type !== 'natural_weapon') continue;
        const match = matchesGate(candidate);
        if (match === null) return { unknown: 'attack-weapon-form-def' };
        if (match) return inventoryComposite(candidate, type);
      }
    }
    if (natural.length > 0) {
      const pick = naturalPick(true);
      if (pick !== '') return pick;
    }
    return { unknown: 'attack-no-matching-weapon' }; // PHP warns and exits before the gate
  }

  // Branch 3: the honoured pin, else resolve_caster_default_item_of_type('weapon') and the priority walk.
  const pin = pinResolution();
  if (pin !== 'walk') return pin;
  let fallback: Row | null = null;
  const counts: Array<[string, string]> = [[THROW_HAND_SLOTS.both_hands, 'count_both_hands_weapon_equipped'],
    [THROW_HAND_SLOTS.main_hand, 'count_main_hand_weapon_equipped'], [THROW_HAND_SLOTS.off_hand, 'count_off_hand_weapon_equipped']];
  walk: for (const [slot, countKey] of counts) {
    if (Math.trunc(Number(scalar(read.value(caster, countKey)) ?? 0)) <= 0) continue;
    for (const candidate of inventoryRows) {
      if (Number(scalar(read.inventoryField(candidate, 'inventory_deleted')) ?? 0) !== 0) continue;
      if (Number(scalar(read.inventoryField(candidate, 'inventory_private')) ?? 0) === 2) continue;
      const type = typeOf(candidate);
      if (type === null) return { unknown: 'attack-weapon-type-unread' };
      if (type.toLowerCase() !== 'weapon') continue;
      if (String(read.inventoryField(candidate, 'equipped') ?? '') === slot) { fallback = candidate; break walk; }
    }
  }
  const fallbackSlot = fallback ? String(read.inventoryField(fallback, 'equipped') ?? '') : '';
  if (fallback && fallbackSlot === THROW_HAND_SLOTS.both_hands) return inventoryComposite(fallback, 'weapon');
  if (natural.length > 0 && looselyZero(read.value(caster, 'count_main_hand_weapon_equipped'))) {
    const pick = naturalPick(false);
    return pick !== '' ? pick : { unknown: 'attack-no-matching-weapon' };
  }
  if (fallback) return inventoryComposite(fallback, 'weapon');
  if (natural.length > 0) {
    const pick = naturalPick(false);
    return pick !== '' ? pick : { unknown: 'attack-no-matching-weapon' };
  }
  return { unknown: 'attack-no-matching-weapon' };
}
